import { z } from 'zod';
import { paginationFields, type PaginatedDto } from './_pagination.js';
import { dateOnlySchema } from './patients.js';

/**
 * Journal d'activité du cabinet : ce que ses membres, la plateforme et les tâches planifiées ont
 * fait dans le cabinet actif. **Réservé au propriétaire.**
 *
 * Distinct du journal de la console (`platform-admin.ts`) : celui-ci ne lit qu'un cabinet, sous sa
 * propre politique d'isolation, et la console ne lit que ses propres gestes. Les deux partagent le
 * verbe stable de `recordAudit` et, côté écran, la même table de libellés.
 *
 * **Rien de brut n'en sort** : ni l'avant ni l'après, ni l'adresse IP, ni l'agent utilisateur, ni
 * l'identifiant de l'objet touché. Le verbe, l'instant, la catégorie et le nom de l'auteur quand
 * c'est un membre — de quoi répondre à « qui a fait quoi, et quand », sans rouvrir un dossier.
 */

/**
 * Catégories du filtre, chacune regroupant des familles de verbes (le préfixe avant le point).
 * Une famille absente de cette table reste lisible dans la liste complète, elle n'a simplement pas
 * de filtre à elle : ajouter un module qui journalise veut dire ranger sa famille ici.
 */
export const auditLogCategories = [
  'patients',
  'agenda',
  'clinical',
  'documents',
  'billing',
  'cabinet',
  'integrations',
  'tasks',
] as const;
export type AuditLogCategory = (typeof auditLogCategories)[number];

export const auditLogCategoryFamilies: Readonly<Record<AuditLogCategory, readonly string[]>> = {
  patients: ['patient', 'patient_review', 'public_registration'],
  agenda: [
    'appointment',
    'appointment_series',
    'availability',
    'booking',
    'booking_widget',
    'consultation_reason',
    'consultation_reason_category',
    'consultation_type',
  ],
  clinical: [
    'encounter',
    'follow_up',
    'follow_up_template',
    'medical_letter',
    'questionnaire_template',
  ],
  documents: ['document', 'pdf'],
  billing: [
    'billing',
    'credit_note',
    'dunning',
    'invoice',
    'member_terms',
    'payment',
    'payment_order',
    'subscription',
  ],
  cabinet: ['account', 'location', 'member', 'organization'],
  integrations: ['docalia', 'doctena', 'integration'],
  tasks: ['task'],
};

/** Famille d'un verbe : `patient` pour `patient.created`. */
export function auditActionFamily(action: string): string {
  return action.split('.', 1)[0] ?? action;
}

export function auditLogCategoryOf(action: string): AuditLogCategory | null {
  const family = auditActionFamily(action);
  return auditLogCategories.find((key) => auditLogCategoryFamilies[key].includes(family)) ?? null;
}

export const organizationAuditLogQuerySchema = z
  .object({
    /** Jours civils du cabinet, bornes incluses. */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    /** Membre du cabinet auteur des gestes. Un membre d'un autre cabinet ne désigne personne. */
    memberId: z.uuid().optional(),
    category: z.enum(auditLogCategories).optional(),
    ...paginationFields,
  })
  .strict()
  .refine((value) => value.from === undefined || value.to === undefined || value.to >= value.from, {
    message: 'La fin de période précède son début',
    path: ['to'],
  });
export type OrganizationAuditLogQuery = z.infer<typeof organizationAuditLogQuerySchema>;

/**
 * Qui a agi. `member` : un membre du cabinet, nommé. `platform` : un compte qui n'en est pas
 * membre — l'équipe de la plateforme posant une offre ou une suspension —, jamais nommé ici.
 * `system` : personne, une tâche planifiée ou un patient passé par une page publique.
 */
export const auditActorKinds = ['member', 'platform', 'system'] as const;
export type AuditActorKind = (typeof auditActorKinds)[number];

export interface OrganizationAuditEntryDto {
  id: string;
  createdAt: string;
  /** Verbe stable en snake_case, ex. `patient.created`. L'écran le traduit. */
  action: string;
  category: AuditLogCategory | null;
  actorKind: AuditActorKind;
  /** Renseignés pour un membre seulement. */
  actorMemberId: string | null;
  actorName: string | null;
}

export type OrganizationAuditListDto = PaginatedDto<OrganizationAuditEntryDto>;
